import React, { useCallback, useEffect, useState } from 'react';
import Table from '../../components/Table';
import { observer } from 'mobx-react-lite';
import Button from '../../components/Button';
import { useHistory, Link } from 'react-router-dom';

import {
    FaCheck,
    FaTimes,
    FaLongArrowAltRight,
    FaSearch,
    FaUndoAlt,
} from 'react-icons/fa';
import Pagination from './../../components/Pagination';
import translations from '../../translations/de';
import debounce from 'lodash.debounce';
import Input from '../../components/Input';
import Checkbox from '../../components/Checkbox';
import InputSeason from '../../components/InputSeason/InputSeason';
import PassesNotifications from '../../components/PassesNotifications';
import ExportBtn from '../../components/ExportBtn';
import TixxConnectBtn from '../../components/TixxConnectBtn';
import API from '../../api/passes';

const Passes = ({ store }) => {
    const history = useHistory();
    const [searchQuery, setSearchQuery] = useState(store.searchQuery);
    const [selectedSeason, setSelectedSeason] = useState(null);
    const [sendNotifications, setSendNotifications] = useState(false);
    const [showDeleted, setShowDeleted] = useState(false);

    const getPasses = useCallback(() => {
        selectedSeason && store.getPasses(searchQuery, selectedSeason, showDeleted);
    }, [store, searchQuery, selectedSeason, showDeleted]);

    useEffect(() => {
        getPasses();
    }, [store, store.selectedPage, selectedSeason, getPasses]);

    const handlePass = (id) => {
        store.selectPass(undefined);
        history.push(`/abos/${id}`);
    };

    const onSearchChangeHandler = debounce((e) => {
        store.setSelectedPage(1);
        setSearchQuery(e.target.value);
        store.setSearchQuery(e.target.value);
    }, 500);

    const isNotDeletedAlready = (row) => {
        return !row.original.deleted_at;
    };

    return !sendNotifications ? (
        <>
            <div className="tab-wrapper">
                <div className="tab-controls">
                    <Input
                        label={translations.search}
                        placeholder={translations.search}
                        defaultValue={store.searchQuery}
                        onChange={onSearchChangeHandler}
                    />
                    <InputSeason
                        selectedSeason={selectedSeason}
                        setSelectedSeason={setSelectedSeason}
                    />
                    {/* Off by default: the list stays as it was, and a deleted
                        Abo is one tick away instead of invisible. Deleted rows
                        come back dimmed (Table greys out anything with
                        deleted_at). */}
                    <Checkbox
                        text={translations.show_deleted}
                        checked={showDeleted}
                        onChange={(e) => {
                            store.setSelectedPage(1);
                            setShowDeleted(e.target.checked);
                        }}
                        outerStyle={{ alignSelf: 'center' }}
                    />
                </div>
                <div className="tab-controls_advanced">
                    {/* Checks the Tixx webhook subscriptions and revives the
                        suspended ones. It lives here because this is the list
                        that goes stale when Tixx stops delivering. */}
                    <TixxConnectBtn />
                    <Button
                        label={translations.notifications}
                        onClick={() => setSendNotifications(true)}
                    />
                    <ExportBtn
                        label={translations.ticket_export}
                        fileName="Passes"
                        apiFunc={() => API.exportPasses({ params: { season_id: selectedSeason } })}
                    />
                </div>
            </div>
            {store.passes && store.passes.length !== 0 && (
                <Table
                    tablePagination={
                        store.totalPages > 1 && (
                            <Pagination
                                totalPages={store.totalPages}
                                selectedPage={store.selectedPage}
                                onSelectedPageChange={(selected) => store.setSelectedPage(selected)}
                            />
                        )
                    }
                    isRowClickable={false}
                    tableName={translations.passes}
                    columns={[
                        {
                            Header: 'ID',
                            accessor: 'id',
                            Cell: ({ value }) => <div>{value}</div>,
                        },
                        {
                            Header: 'Ticket-Nr',
                            accessor: 'ticket_id',
                            Cell: ({ value }) => <div>{value ? value : '-'}</div>,
                        },
                        {
                            Header: 'BarCodes',
                            accessor: 'barcode',
                            Cell: ({ value }) => <div>{value}</div>,
                        },
                        {
                            Header: translations.assign_token,
                            accessor: 'assign_token',
                            Cell: ({ value }) => <div>{value}</div>,
                        },
                        {
                            Header: 'Firma',
                            accessor: 'company',
                            Cell: ({ value }) => <div>{value ? value : '-'}</div>,
                        },
                        {
                            Header: translations.name,
                            accessor: 'name',
                            Cell: ({ value }) => <div>{value ? value : '-'}</div>,
                        },
                        {
                            Header: translations.user,
                            accessor: 'user',
                            Cell: ({ value }) => (
                                <div
                                    onClick={() =>
                                        value?.id
                                            ? history.push({
                                                  pathname: `/fans/${value.id}`,
                                                  state: {
                                                      activeTabName: 'Abos',
                                                      selectedSeason,
                                                  },
                                              })
                                            : null
                                    }
                                >
                                    {value ? (
                                        <span className="user_link">
                                            <FaLongArrowAltRight />{' '}
                                            <span>{`${value.first_name} ${value.last_name}`}</span>
                                        </span>
                                    ) : (
                                        '-'
                                    )}
                                </div>
                            ),
                        },
                        {
                            Header: translations.accesses,
                            accessor: 'etickets',
                            Cell: ({ row, value }) => (
                                <div>
                                    {value ? (
                                        <Link to={`/etickets?pass_id=${row.cells[0].value}`}>
                                            <FaLongArrowAltRight />{' '}
                                            {`${translations.show} [${value}]`}
                                        </Link>
                                    ) : (
                                        '0'
                                    )}
                                </div>
                            ),
                        },
                        {
                            Header: translations.can_claim_freunde,
                            accessor: 'can_claim_freunde',
                            Cell: ({ value, row }) => (
                                <div>
                                    {value === true ? (
                                        <FaCheck className="confirm" />
                                    ) : (
                                        <FaTimes className="error" />
                                    )}
                                </div>
                            ),
                        },
                        {
                            Header: translations.seat,
                            accessor: 'seat',
                            Cell: ({ value }) => (
                                <div>
                                    Entrance: {value?.seat_group?.sector?.title} <br />
                                    Block: {value?.seat_group?.title} <br />
                                    Row: {value?.row} <br />
                                    Seat: {value?.num} <br />
                                </div>
                            ),
                        },

                        {
                            Header: '',
                            accessor: 'action',
                            Cell: ({ row }) => (
                                <div className="button-cell">
                                    <Button
                                        onClick={() => {
                                            handlePass(row.cells[0].value);
                                        }}
                                        title={translations.show}
                                    >
                                        <FaSearch />
                                    </Button>

                                    {/* Abos come from Tixx and are only mirrored here. Deleting one
                                        in this panel hid it from us while it stayed alive at Tixx, and a
                                        soft-deleted access is never recreated — the ticket simply became
                                        invisible. Deletion belongs in the Tixx panel. Revive stays, for
                                        rows deleted before this. */}
                                    {!isNotDeletedAlready(row) && (
                                        <Button onClick={() => store.revivePass(row.cells[0].value)}>
                                            <FaUndoAlt />
                                        </Button>
                                    )}
                                </div>
                            ),
                        },
                    ]}
                    data={store.passes.map(
                        ({
                            id,
                            ticket_id,
                            assign_token,
                            type,
                            user,
                            accesses,
                            accesses_num,
                            barcodes_num,
                            seat,
                            can_claim_freunde,
                            season,
                            first_name,
                            last_name,
                            company,
                            deleted_at,
                        }) => {
                            return {
                                id,
                                ticket_id,
                                assign_token,
                                type,
                                name: first_name + ' ' + last_name,
                                user,
                                etickets: accesses_num,
                                seat,
                                barcode: barcodes_num,
                                company,
                                can_claim_freunde,
                                deleted_at,
                            };
                        },
                    )}
                />
            )}

        </>
    ) : (
        <PassesNotifications closeScreen={() => setSendNotifications(false)} />
    );
};

export default observer(Passes);
